import { AnonymousListItem } from "@/components/employee/AnonymousListItem.tsx";
import { FormResultForEmployeeElement } from "@/components/form-result/result-modal/components/FormResultForEmployeeElement.tsx";
import { questionnaireTypes } from "@/components/questionnaire/questionnaire.config.ts";
import { useEmployeeDetails } from "@/queries/employees/use-employees.query.ts";
import { ApiFormType } from "@/types/forms/form.types.ts";
import { Box, Stack, Title } from "@skailer-com/uikit";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  questionnaireId: number;
  employeeId?: number;
  respondentUuid?: string;
  type: ApiFormType;
}

export function FormResultForEmployeeModalContent({
  questionnaireId,
  employeeId,
  respondentUuid,
  type,
}: Props) {
  const { t } = useTranslation();

  const questionnaireType = questionnaireTypes[type];
  const { data: participantResults, isPending: isResultsLoading } =
    questionnaireType.hooks.useGetQuestionnaireResults({
      questionnaireId: questionnaireId,
      employeeId: employeeId,
      respondentUuid: respondentUuid,
      enabled: true,
    });
  const { data: employee, isPending: isEmployeeLoading } = useEmployeeDetails(employeeId);

  const isLoading = isResultsLoading || isEmployeeLoading;

  return (
    <>
      <AnonymousListItem
        employee={employee}
        size={"xl"}
        behavior={"card"}
        uuid={!employeeId ? respondentUuid : undefined}
        isLoading={isLoading}
      />
      <Box className={"h-full overflow-y-auto"}>
        {participantResults && (
          <>
            <Stack>
              {participantResults.pages.map((page, pageIndex) => (
                <Fragment key={`${pageIndex}`}>
                  <Stack direction={"horizontal"} gap={"md"}>
                    <Stack items={"end"} className={"w-16 min-h-7 pb-0.5"} justify={"end"}>
                      <Title size={"xs"} header={`${pageIndex + 1}`} />
                    </Stack>
                    <Stack className={"flex-1"} justify={"end"}>
                      <Title
                        header={page.title || t("common:page")}
                        size={"lg"}
                        paddingTop={true}
                      />
                    </Stack>
                  </Stack>
                  {page.sections.map((section, sectionIndex) => (
                    <Fragment key={`${sectionIndex}`}>
                      <Stack direction={"horizontal"} gap={"md"}>
                        <Stack items={"end"} className={"w-16 min-h-7 pb-0.5"} justify={"end"}>
                          <Title size={"xs"} header={`${pageIndex + 1}.${sectionIndex + 1}`} />
                        </Stack>
                        <Stack className={"flex-1"} justify={"end"}>
                          <Title
                            header={section.title || t("common:section")}
                            size={"md"}
                            paddingTop={true}
                          />
                        </Stack>
                      </Stack>
                      {section.elements.map((element, elementIndex) => (
                        <Fragment key={`${elementIndex}`}>
                          <Stack direction={"horizontal"} gap={"md"}>
                            <Stack
                              items={"end"}
                              className={"w-16 min-h-7 pt-6 shrink-0"}
                              justify={"start"}
                            >
                              <Title
                                size={"xs"}
                                header={`${pageIndex + 1}.${sectionIndex + 1}.${elementIndex + 1}`}
                              />
                            </Stack>
                            <Stack
                              className={"flex-1 wrap-break-word overflow-hidden"}
                              justify={"end"}
                            >
                              <FormResultForEmployeeElement
                                element={element}
                                answers={participantResults.answers}
                              />
                            </Stack>
                          </Stack>
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </>
  );
}
